import type { Locale } from '@/helpers/locale-helper';
import type { NewTitlesEdge } from '@/@types/graphql-types';
import type { GetPopularTitlesQuery } from '@/pages/graphql/queries/GetPopularTitles.query';

import { AppDirection } from '@/enums/app-direction';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { hasUCConsentBanner } from '@/helpers/tracking';
import { CollectionType } from '@/enums/collection-type';
import { calculateUrl } from '@/helpers/state-helper';
import {
	COUNTRY_EXCEPTIONS,
	COUNTRY_EXCLUDED,
	COUNTRY_POPULAR_PAGES_WITH_NOINDEX,
} from '@/constants/web-locales.constant';
import { RTL_LANGUAGES } from '@/constants/rtl-languages';

import { defineStore } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';

import { computed, ref } from 'vue';

import { WebLocale } from '@/enums/web-locale';

const FONTS_RESOURCES = ['lato-regular.woff2', 'lato-700.woff2', 'lato-900.woff2', 'anton-regular.woff2'];

function buildHrefLangHref(activeDomain: string, domain: string, tagHref: string, pageParam: number | null): string {
	const url = new URL(`https://${activeDomain}.${domain}${tagHref}`);
	if (pageParam) {
		url.searchParams.set('page', String(pageParam));
	}
	return url.toString();
}

/** Decodes encoded ASCII characters (%E6%98%A0%E7%94%BB -> 映画). */
function decodeUrl(url: string) {
	try {
		return decodeURIComponent(url);
	} catch {
		// Malformed % sequences — return as-is
		return url;
	}
}

// we have different pagination limits based on levels:
// - level 0: no pagination
// - level 1: pagination enabled up to ?page=4
// - level 2: pagination enabled up to ?page=6
// - level 3: pagination enabled up to ?page=10
interface PaginationLevelConfig {
	level: number;
	weblocales: WebLocale[];
	pages: number;
}
export const PAGINATION_LEVELS = {
	3: { level: 3, weblocales: [], pages: 10 },
	2: {
		level: 2,
		weblocales: [
			WebLocale.us,
			WebLocale.uk,
			WebLocale.ca,
			WebLocale.au,
			WebLocale.de,
			WebLocale.es,
			WebLocale.fr,
			WebLocale.in,
			WebLocale.mx,
		],
		pages: 6,
	},
	1: { level: 1, weblocales: [WebLocale.br, WebLocale.it, WebLocale.cz, WebLocale.hu], pages: 4 },
	0: { level: 0, weblocales: [], pages: 0 },
} as Record<number, PaginationLevelConfig>;
export type PaginationLevel = keyof typeof PAGINATION_LEVELS;

// DEFAULT Meta Values
const DEFAULT_META = {
	title: 'JustWatch - The Streaming Guide',
	description: 'All your streaming services in one app.',
	meta: [
		{ charset: 'utf-8' },
		{ httpEquiv: 'X-UA-Compatible', content: 'IE=edge' },
		{ name: 'viewport', content: 'viewport-fit=cover, width=device-width, initial-scale=1' },

		// facebook OG
		{ property: 'og:site_name', content: 'JustWatch' },
		{ property: 'fb:app_id', content: '794243977319785' },
		{ vmid: 'og:image', property: 'og:image', content: `/${ASSETS_DIR}/img/JustWatch_logo_with_claim.png` },
		{ vmid: 'og:image:width', property: 'og:image:width', content: '606' },
		{ vmid: 'og:image:height', property: 'og:image:height', content: '302' },
		{ name: 'mobile-web-app-capable', content: 'yes' },
		{ name: 'apple-mobile-web-app-capable', content: 'yes' },
		{ name: 'theme-color', content: '#182531' },

		// verifications
		{ name: 'google-site-verification', content: 'AkO0-dINGfhSod2X6LeYIp9hwZc8ShczjzJA2a47dME' },
		{ name: 'facebook-domain-verification', content: 'd61y4roe8t5xwbb0gfu6gwe8j8iq3i' },
		{ name: 'dailymotion-domain-verification', content: 'dmorpwc4h544ct6mv' },
		{ name: 'dailymotion-domain-verification', content: 'dmm2vwzsugkzg13q5' },
	],
};

type Pagination = { prev: number | null; next: number | null; page: number } | null;
export const useMetaStore = defineStore('meta', () => {
	// Router is not initialised in time for the `head` computed
	let router: ReturnType<typeof useRouter> | undefined = undefined;
	try {
		router = useRouter();
	} catch {}

	const routingStore = useRoutingStore();
	const languageStore = useLanguageStore();
	const userStore = useUserStore();
	const filterStore = useFilterStore();

	const runtimeConfig = useRuntimeConfig().public;

	const htmlAttrs = computed(() => ({
		lang: languageStore.language,
		dir: RTL_LANGUAGES.includes(languageStore.language) ? AppDirection.RTL : AppDirection.LTR,
	}));

	const paginationState = ref<Pagination>(null);
	// slight hack: among other checks, this will determine if we add a noindex on a NEW or POPULAR page.
	// using a new prop here, because we don't have the titles of new and popular in any store.
	const visiblePosters = ref(0);

	const appDirection = computed(() => htmlAttrs.value.dir);

	const link = computed(() => {
		// explanation what dns-prefetch & preconnect do and when to use.
		// dns-prefetch is used for resources that might load at some point later.
		// preconnect is used for critical resources that are 100% needed.
		//
		// dns-prefetch:
		// - DNS Lookup (Resolves domain to IP address; 20-150ms)
		//
		// preconnect:
		// - DNS Lookup (Resolves domain to IP address; 20-150ms)
		// - TCP Handshake (Establishes connection to server; 50-150ms)
		// - TLS Negotiation (Establishes a secure connection, if HTTPS; 100-300ms)

		const pageDnsPrefetch: any[] = [];
		if (hasUCConsentBanner(languageStore.webLocale)) {
			// do not add dns-prefetch if UC won't be loaded anyway
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://app.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://graphql.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://api.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://consents.usercentrics.eu' });
		}

		let link = [
			...pageDnsPrefetch,
			{ rel: 'preconnect', href: 'https://images.justwatch.com' },

			// PRELOADS: fonts
			...FONTS_RESOURCES.filter(
				font =>
					// Anton-Regular isn't used on popular, new and sports; therefore don't preload it
					!(
						font.includes('anton-regular') &&
						['popular', 'new', 'sports'].includes(routingStore.activeRoute?.meta?.collectionType)
					)
			).map(font => ({
				rel: 'preload',
				href: `/${ASSETS_DIR}/fonts/${font}`,
				as: 'font',
				type: 'font/woff2',
				crossorigin: true,
			})),

			// Favicon
			{ rel: 'icon', href: `/${ASSETS_DIR}/favicon.ico` },
		];

		if (runtimeConfig.ssrMode) {
			link = [...link, ...runtimeConfig.httpPreconnect, ...runtimeConfig.dnsPrefetch];
		}

		return link;
	});

	const paginationLevel = computed(() => {
		if (userStore.isLoggedIn) return 0;

		const webLocale = languageStore.webLocale;
		const match = Object.values(PAGINATION_LEVELS).find(item => item.weblocales.includes(webLocale));

		return match?.level ?? 0;
	});

	// it will set noindex for any page whose page is >= 2.
	const pagination = computed(() => {
		const isGuidesHome = routingStore.activeRoute?.name === 'app.editorial.subdomain.home';
		const isEnabledOnPage =
			isGuidesHome ||
			(routingStore.activeCollectionType !== null &&
				[CollectionType.POPULAR].includes(routingStore.activeCollectionType));

		// google's end of november's pagination guide says to not use noindex for page param pages.
		// so we're first testing this behaviour only in 1 country.
		// update 2024-01-04: now all pagination countries support the november pagination guide
		const hasPagination = isGuidesHome || paginationLevel.value > 0;
		if (!hasPagination || !isEnabledOnPage) {
			return { paginationMetas: [] };
		}

		const { page, exclude_providers, exclude_genres, ...restQuery } = (routingStore.activeRoute?.query ??
			{}) as Record<string, string>;
		void exclude_providers;
		void exclude_genres;

		const hasFilteredQueryParams = Object.keys(restQuery).length >= 1;
		const pageNumber = page && !isNaN(page as any) && Number(page) ? parseInt(page) : 1;
		// check if `noindex, follow` needs to be set for page > 1 (pre-november pagination guide)
		const robots =
			hasPagination && hasFilteredQueryParams
				? { vmid: 'robots', name: 'robots', content: 'noindex, follow' }
				: undefined;
		// make sure paginated pages have a canonical to themselves with the page query param.

		const paginationMetas = [robots].filter(Boolean);
		const urlMetadata = routingStore.urlMetadata;
		const activeDomain = routingStore.activeDomain;

		const paginationCanonical =
			hasPagination && !hasFilteredQueryParams && pageNumber > 1
				? {
						vmid: 'canonical',
						rel: 'canonical',
						href: decodeUrl(
							`https://${activeDomain}.${runtimeConfig.jwConfig.DOMAIN}${urlMetadata?.fullPath}?page=${pageNumber}`
						),
				  }
				: null;
		return { paginationMetas, paginationCanonical, paginationNumber: pageNumber };
	});

	const head = computed(() => {
		const urlMetadata = routingStore.urlMetadata;

		/* When tab title starts with LTR direction word (in most cases it is - JustWatch or Movie Name),
		browser will automatically display whole title with LTR direction. When RTL language chosen we will force
		browser to display tab title (also meta description) in RTL direction by adding hidden RTL unicode character */
		const rtlUnicode = appDirection.value === AppDirection.RTL ? '\u202b' : '';

		const title = rtlUnicode + (urlMetadata?.metaTitle || DEFAULT_META.title);
		const description = rtlUnicode + (urlMetadata?.metaDescription || DEFAULT_META.description);
		const keywords = urlMetadata?.metaKeywords || '';
		const activeDomain = routingStore.activeDomain;

		let canonicalUrl = urlMetadata?.fullPath ?? routingStore.activeRoute?.fullPath.split('?')[0];

		const isComingSoon = routingStore.activeRoute?.name?.includes('coming-soon');
		const isUpcomingCountry = ['de', 'es', 'uk', 'us', 'fr'].includes(languageStore.webLocale);
		if (isComingSoon && isUpcomingCountry) {
			const constantStore = useConstantStore();

			canonicalUrl = calculateUrl(
				router,
				undefined,
				CollectionType.UPCOMING,
				filterStore.currentFilters,
				constantStore.providersByShortName,
				constantStore.genresByShortName,
				constantStore.subgenresList
			);
		}

		const isAppdev =
			runtimeConfig.jwConfig.DOMAIN.startsWith('prod-content') ||
			runtimeConfig.jwConfig.DOMAIN.startsWith('moviecycle');
		const url =
			process.env.NODE_ENV === 'development'
				? `http://localhost:8080${canonicalUrl}`
				: `https://${isAppdev && activeDomain === Domain.MAIN ? '' : `${activeDomain}.`}${
						runtimeConfig.jwConfig.DOMAIN
				  }${canonicalUrl}`;

		const { paginationMetas, paginationCanonical, paginationNumber } = pagination.value;

		// we want to lower our CCNI URLs share (URLs that Google crawls but doesnt index).
		// since these parameterised URLs have canonicals to other pages anyway, we can set them to `noindex, follow`.
		// const hasQueryParamWithoutPagination = routing.activeRoute?.fullPath.includes('?') && !paginationCanonical;
		const { metaRobots = '' } = urlMetadata ?? {};
		const {
			page: _page,
			exclude_providers,
			exclude_genres,
			...restQuery
		} = (routingStore.activeRoute?.query ?? {}) as Record<string, string>;
		void exclude_providers;
		void exclude_genres;
		void _page;
		const hasFilteredQueryParams = Object.keys(restQuery).length >= 1;

		const noIndexThroughPostersAndParameterisedUrls =
			// only on NEW or POPULAR pagetypes
			[
				CollectionType.POPULAR,
				CollectionType.EDITORIAL,
				CollectionType.NEW,
				CollectionType.PRICEDROPS,
				CollectionType.UPCOMING,
			].includes(routingStore.activeRoute?.meta?.collectionType) &&
			// doesn't override robots from URL or pagination
			!metaRobots &&
			!paginationMetas.find((item: any) => item.name === 'robots') &&
			// is not in a pagination
			!paginationCanonical &&
			// has less than 5 posters on the pages, so we only noindex irrelevant pages
			visiblePosters.value <= 5 &&
			// has filtered query params
			hasFilteredQueryParams;

		// temp solution to set debug params in session storage
		if (import.meta.client && localStorage.getItem('no_index_debug') === 'true') {
			sessionStorage.setItem(
				'noIndexDebug',
				JSON.stringify({
					result: noIndexThroughPostersAndParameterisedUrls,
					'1-isCollectionType': [
						CollectionType.POPULAR,
						CollectionType.EDITORIAL,
						CollectionType.NEW,
						CollectionType.PRICEDROPS,
						CollectionType.UPCOMING,
					].includes(routingStore.activeRoute?.meta?.collectionType),
					'2-hasNoMetaRobots': !metaRobots,
					'2-hasNoPaginationMeta': !paginationMetas.some((item: any) => item.name === 'robots'),
					'3-hasNoCanonical': !paginationCanonical,
					'4-lessThan5Posters': (visiblePosters.value || 0) <= 5,
					'5-hasFilteredQueryParams': hasFilteredQueryParams,
					visiblePosters: visiblePosters.value,
				})
			);
		}

		const meta = [
			{ vmid: 'og:title', property: 'og:title', content: title },
			// keywords
			{ name: 'keywords', content: keywords },
			// description
			{ vmid: 'description', name: 'description', content: description },
			{
				vmid: 'og:description',
				property: 'og:description',
				content: description,
			},
			{ vmid: 'og:url', property: 'og:url', content: url },
			// pages with `page=2` and higher param should
			...paginationMetas,
		] as any;

		const hasNoIndexParameter =
			routingStore.activeRoute &&
			Object.keys(routingStore.activeRoute?.query ?? {}).includes('_escaped_fragment_');
		// escaped_fragment query param urls were historically used for internal testing reasons.
		// Google is crawling them and they're showing up on GSC reports, so we need to no index them.

		if (noIndexThroughPostersAndParameterisedUrls || hasNoIndexParameter)
			// on NEW, POPULAR, EDITORIAL page types we check the amount of titles on the page.
			// if there are not many, less than 5, we turn that page into `noindex, follow`.

			// we also set noindex when google crawls URLs with internal testing parameters
			meta.push({ vmid: 'robots', name: 'robots', content: 'noindex, follow' });

		if (urlMetadata?.metaRobots) {
			meta.push({ vmid: 'robots', name: 'robots', content: urlMetadata.metaRobots });
		}

		let headHrefLangTags = [...(urlMetadata?.hrefLangTags ?? [])];
		// we exclude the guide from this, because they get their hreflang tags from the backend
		const isGuideHome = routingStore.activeRoute?.name === 'app.editorial.subdomain.home';
		if (!!paginationCanonical && !isGuideHome) {
			// pagination-aware hreflang: on paginated pages:
			// drop locales that don't support this ?page=x depth and add ?page= query parameter
			headHrefLangTags = headHrefLangTags
				.filter(item => {
					const [, country] = item.locale.split('_');
					const normalized = country.toLowerCase();
					const webLocale = (COUNTRY_EXCEPTIONS[normalized] ?? normalized) as WebLocale;

					// find which level this country belongs to
					const level = Object.values(PAGINATION_LEVELS).find(({ weblocales }) =>
						weblocales.includes(webLocale)
					);

					// keep the hreflangtag only if the country's level supports this page number
					return (level?.pages ?? 0) >= paginationNumber;
				})
				.map(item => {
					const [path, query = ''] = item.href.split('?');
					const params = new URLSearchParams(query);
					params.set('page', String(paginationNumber));
					return { ...item, href: `${path}?${params.toString()}` };
				});
		}

		// Temporary hacky solution to add the hreflang tags to Home. In SPA they are manually set for home.
		// Once the values are added to `urlMetadata.hrefLangTags`, this can be removed (imports too!).
		if (routingStore.activeRoute?.name === 'app.home' && urlMetadata?.hrefLangTags == null) {
			// language.locales is usually on-demand only.
			// it's requested for home, sports, and language & country selection modals
			headHrefLangTags = ((languageStore.locales || []) as Locale[])
				.filter(locale => !COUNTRY_EXCLUDED[locale.webLocale])
				.filter(locale => !COUNTRY_POPULAR_PAGES_WITH_NOINDEX.includes(locale.webLocale))
				.map(locale => ({
					href: `/${locale.webLocale}`, // doesn't need to be decoded as it's always jp, kr, us, etc.
					hrefLang: `${locale.full_locale}`,
					locale: locale.full_locale,
					hasNoindex: false,
				}));
		}

		// add hreflang x-default tag to home and popular page.
		// special case: if popular page contains robots noindex, then don't display x-default hreflang tag.
		if (['app.home', 'app.titles.popular.list'].includes(routingStore.activeRoute?.name || '')) {
			const { metaRobots = '' } = urlMetadata ?? {};
			const hasNoIndex = metaRobots?.includes('noindex');

			if (!hasNoIndex) {
				headHrefLangTags.push({ href: '/', hrefLang: 'x-default' } as (typeof headHrefLangTags)[number]);
			}
		}

		// Include page query parameter in hreflang hrefs on Guide Home
		const parsedPage = parseInt(_page);
		const pageParam = isGuideHome && parsedPage > 1 ? parsedPage : null;

		// Use activeDomain for canonical URLs to ensure they point to the domain the user is currently on
		// This is important when routes exist on multiple domains (e.g., sports on both main and sports subdomain)
		const _link = [
			// improve overall head getter performance
			// static links
			...link.value,
			// links that change at every page change
			paginationCanonical ?? {
				vmid: 'canonical',
				rel: 'canonical',
				href: decodeUrl(`https://${activeDomain}.${runtimeConfig.jwConfig.DOMAIN}${canonicalUrl}`),
			},
			...headHrefLangTags.map((tag: { href: string; hrefLang: string }) => ({
				rel: 'alternate',
				href: decodeUrl(buildHrefLangHref(activeDomain, runtimeConfig.jwConfig.DOMAIN, tag.href, pageParam)),
				hreflang: tag.hrefLang.replace('_', '-'),
			})),
		];

		return {
			title,
			meta: [...DEFAULT_META.meta, ...meta],
			link: _link,
			htmlAttrs: htmlAttrs.value,
		};
	});

	function setVisiblePosters(posters: number) {
		visiblePosters.value = posters;
	}

	function setPopularPageJsonLD(popularPageData: GetPopularTitlesQuery['popularTitles']) {
		setVisiblePosters((popularPageData.edges || []).length);
	}

	function setNewPageJsonLD(newPageData: NewTitlesEdge[]) {
		setVisiblePosters(newPageData.length);
	}

	function setPagination(paginationData: Pagination) {
		paginationState.value = paginationData;
	}

	return {
		paginationState,
		visiblePosters,

		appDirection,
		link,
		pagination,
		head,
		paginationLevel,

		setVisiblePosters,
		setPopularPageJsonLD,
		setNewPageJsonLD,
		setPagination,
	};
});

export type MetaStoreType = ReturnType<typeof useMetaStore>;
